import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { getBookings, updateBookingStatus, Booking } from '../services/bookingService';
import { settingsService } from '../services/settingsService';
import Visibility from '@mui/icons-material/Visibility';
import {
  Box,
  Button,
  Chip,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Badge,
  TablePagination,
  IconButton
} from '@mui/material';

import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  AccessTime as PendingIcon,
  Build as InProgressIcon,
  DirectionsCar as DirectionsCarIcon,
  LocalCarWash as LocalCarWashIcon,
  LocationOn as LocationOnIcon,
  PlayArrow as PlayArrowIcon,
  Stop as StopIcon,
  Person as PersonIcon
} from '@mui/icons-material';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`bookings-tabpanel-${index}`}
      aria-labelledby={`bookings-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 0 }}>{children}</Box>}
    </div>
  );
}

type BookingStatus =
  | 'pending'
  | 'confirmed'
  | 'work_in_progress'
  | 'completed'
  | 'cancelled'
  | 'rejected'
  | 'assign_washer'
  | 'pickup'
  | 'drop'
  | 'arrived_at_the_location'
  | 'at_the_service_station'
  | 'start_service'
  | 'end_service';

interface BookingTableProps {
  bookings: Booking[];
  loading: boolean;
  error: string | null;
  onStatusUpdate: (id: number, status: BookingStatus) => Promise<void>;
}

const BookingTable: React.FC<BookingTableProps> = ({
  bookings,
  loading,
  error,
  onStatusUpdate,
}) => {
  const navigate = useNavigate();

  const formatTime = (timeString: string) => {
    return format(parseISO(`2000-01-01T${timeString}`), 'HH:mm');
  };

  const formatDate = (dateString: string) => {
    return format(parseISO(dateString), 'dd/MM/yyyy');
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (bookings.length === 0) {
    return (
      <Box textAlign="center" p={5}>
        <Typography color="textSecondary">No bookings found</Typography>
      </Box>
    );
  }

  return (
    <TableContainer 
      component={Paper} 
      sx={{ 
        borderRadius: 2, 
        boxShadow: 1,
        backgroundColor: 'background.paper',
        '& .MuiTableCell-root': {
          color: 'text.primary'
        }
      }}
    >
      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>Booking #</TableCell>
            <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>Customer</TableCell>
            <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>Date & Time</TableCell>
            <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>Amount</TableCell>
            <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>Staff payout</TableCell>
            <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>Commission</TableCell>
            <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>Status</TableCell>
            <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {bookings.map((b) => (
            <TableRow 
              key={b.booking_id}
              sx={{
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            >
              <TableCell sx={{ color: 'text.primary' }}>{b.booking_number}</TableCell>
              <TableCell sx={{ color: 'text.primary' }}>
                <Box display="flex" flexDirection="column">
                  <Typography fontWeight={500} color="text.primary">
                    {b.contact_person_name}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell sx={{ color: 'text.primary' }}>
                <Box display="flex" flexDirection="column">
                  <Typography color="text.primary">{formatDate(b.booking_date)}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatTime(b.start_time)} - {formatTime(b.end_time)}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell sx={{ color: 'text.primary' }}>{process.env.REACT_APP_SITE_CURRENCY}{b.final_amount.toFixed(2)}</TableCell>
               <TableCell sx={{ color: 'text.primary' }}>
                {process.env.REACT_APP_SITE_CURRENCY}
                {(b.final_amount - (b.admin_commission || 0)).toFixed(2)}
              </TableCell>
              <TableCell sx={{ color: 'text.primary' }}>
                {process.env.REACT_APP_SITE_CURRENCY}
                {(b.admin_commission || 0).toFixed(2)}
              </TableCell>
              <TableCell sx={{ color: 'text.primary' }}>
                {b.status === 'pending' && (
                  <Chip icon={<PendingIcon />} label="Pending" color="warning" size="small" variant="outlined" />
                )}
                {b.status === 'confirmed' && (
                  <Chip icon={<CheckCircleIcon />} label="Confirmed" color="success" size="small" variant="outlined" />
                )}
                {b.status === 'assign_washer' && (
                  <Chip icon={<PersonIcon />} label="Assign Washer" color="info" size="small" variant="outlined" />
                )}
                {b.status === 'pickup' && (
                  <Chip icon={<DirectionsCarIcon />} label="Pickup" color="info" size="small" variant="outlined" />
                )}
                {b.status === 'arrived_at_the_location' && (
                  <Chip icon={<LocationOnIcon />} label="Arrived" color="info" size="small" variant="outlined" />
                )}
                {b.status === 'at_the_service_station' && (
                  <Chip icon={<LocalCarWashIcon />} label="At Station" color="info" size="small" variant="outlined" />
                )}
                {b.status === 'start_service' && (
                  <Chip icon={<PlayArrowIcon />} label="Service Started" color="info" size="small" variant="outlined" />
                )}
                {b.status === 'work_in_progress' && (
                  <Chip icon={<InProgressIcon />} label="In Progress" color="info" size="small" variant="outlined" />
                )}
                {b.status === 'end_service' && (
                  <Chip icon={<StopIcon />} label="Service Ended" color="info" size="small" variant="outlined" />
                )}
                {b.status === 'drop' && (
                  <Chip icon={<DirectionsCarIcon />} label="Drop Off" color="primary" size="small" variant="outlined" />
                )}
                {b.status === 'completed' && (
                  <Chip icon={<CheckCircleIcon />} label="Completed" color="success" size="small" variant="outlined" />
                )}
                {b.status === 'cancelled' && (
                  <Chip icon={<CancelIcon />} label="Cancelled" color="error" size="small" variant="outlined" />
                )}
                {b.status === 'rejected' && (
                  <Chip icon={<CancelIcon />} label="Rejected" color="error" size="small" variant="outlined" />
                )}
              </TableCell>
              <TableCell sx={{ color: 'text.primary' }}>
                <IconButton
                  component={Link}
                  to={`/bookings/${b.booking_id}`}
                  color="primary"
                  size="small"
                  title="View Booking"
                  sx={{
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  <Visibility fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const Bookings: React.FC = () => {
  const [allBookings, setAllBookings] = useState<Booking[]>([]);
  const [filteredBookings, setFilteredBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [counts, setCounts] = useState({
    all: 0,
    pending: 0,
    confirmed: 0,
    inProgress: 0,
    completed: 0,
    cancelled: 0
  });

  const fetchCounts = async () => {
    try {
      const [all, pending, confirmed, inProgress, completed, cancelled] = await Promise.all([
        getBookings(1, 1),
        getBookings(1, 1, 'pending'),
        getBookings(1, 1, 'confirmed'),
        getBookings(1, 1, 'work_in_progress'),
        getBookings(1, 1, 'completed'),
        getBookings(1, 1, 'cancelled')
      ]);

      setCounts({
        all: all.pagination?.total || 0,
        pending: pending.pagination?.total || 0,
        confirmed: confirmed.pagination?.total || 0,
        inProgress: inProgress.pagination?.total || 0,
        completed: completed.pagination?.total || 0,
        cancelled: cancelled.pagination?.total || 0
      });
    } catch (err) {
      console.error('Failed to fetch counts:', err);
    }
  };

  const fetchBookings = async (currentPage = 0, status: string | null = null) => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch the actual bookings for the current view with the specified status
      const result = await getBookings(currentPage + 1, rowsPerPage, status || undefined);
      console.log('Bookings API Response:', result);
      
      if (!result || !result.bookings) {
        throw new Error('Invalid response format from server');
      }
      
      const { bookings, pagination } = result;
      
      // Update counts on first load or when status changes
      if (currentPage === 0) {
        try {
          await fetchCounts();
        } catch (countError) {
          console.error('Error fetching counts:', countError);
          // Continue with bookings even if counts fail
        }
      }

      try {
        // Get admin commission and calculate commission for each booking
        const adminCommission = await settingsService.getAdminCommission();
        console.log('Admin Commission:', adminCommission);
        
        const bookingsWithCommission = bookings.map(booking => {
          // Ensure final_amount is a number
          const finalAmount = typeof booking.final_amount === 'number' ? booking.final_amount : 0;
          return {
            ...booking,
            admin_commission: finalAmount * ((adminCommission || 0) / 100)
          };
        });

        setAllBookings(bookingsWithCommission);
        setFilteredBookings(bookingsWithCommission);
        setTotalItems(pagination?.total || 0);
      } catch (commissionError) {
        console.error('Error processing commissions:', commissionError);
        // If commission calculation fails, still show bookings without commission
        setAllBookings(bookings);
        setFilteredBookings(bookings);
        setTotalItems(pagination?.total || 0);
      }
    } catch (err: any) {
      const errorMessage = err?.message || 'Failed to load bookings';
      setError(errorMessage);
      console.error('Error in fetchBookings:', err);
      // Clear bookings on error
      setAllBookings([]);
      setFilteredBookings([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    // Get the current status based on the active tab
    let status = null;
    switch (tabValue) {
      case 1: status = 'pending'; break;
      case 2: status = 'confirmed'; break;
      case 3: status = 'work_in_progress'; break;
      case 4: status = 'completed'; break;
      case 5: status = 'cancelled'; break;
      default: status = null;
    }
    
    fetchBookings(page, status);
  }, [page, tabValue]);

  const handleStatusUpdate = async (id: number, newStatus: BookingStatus) => {
    try {
      await updateBookingStatus(id, newStatus);
      
      // Update the local state immediately for a responsive UI
      const updated = allBookings.map((b) =>
        b.booking_id === id ? { ...b, status: newStatus } : b
      );
      
      setAllBookings(updated);
      setFilteredBookings(updated);
      
      // Refresh the counts to ensure they stay in sync with the server
      await fetchCounts();
      
      // Re-fetch the current tab to ensure we have the latest data
      let status = null;
      switch (tabValue) {
        case 1: status = 'pending'; break;
        case 2: status = 'confirmed'; break;
        case 3: status = 'work_in_progress'; break;
        case 4: status = 'completed'; break;
        case 5: status = 'cancelled'; break;
        default: status = null;
      }
      await fetchBookings(page, status);
      
    } catch (err) {
      setError('Failed to update booking status');
      console.error(err);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setPage(0); // Reset to first page when changing tabs
    // The useEffect will handle the data fetching with the new tab value
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box 
      sx={{ 
        flexGrow: 1,
        p: 0,
        
        minHeight: '100%',
        width: '100%'
      }}
    >
      <Box mb={3}>
        <Typography variant="h4" component="h1" color="text.primary" fontWeight="bold">
          Bookings
        </Typography>
      </Box>

      <Box 
        sx={{ 
          backgroundColor: 'background.paper',
          boxShadow: 1,
          borderRadius: 2,
          p: 0
        }}
      >
        <Box className="card-body p-0">
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange}
            sx={{
              '& .MuiTab-root': {
                color: 'text.secondary',
                '&.Mui-selected': {
                  color: 'primary.main',
                },
              },
              '& .MuiTabs-indicator': {
                backgroundColor: 'primary.main',
              },
            }}
          >
  <Tab
    label={
      <Box sx={{ textAlign: "center", lineHeight: 1.2, display: 'flex', flexDirection: 'row', gap: 1, alignItems:'center',}}>
        <Typography variant="body2">ALL</Typography>
        <Typography variant="caption" color="primary">
          ({counts.all})
        </Typography>
      </Box>
    }
  />

  <Tab
    label={
      <Box sx={{ textAlign: "center", lineHeight: 1.2, display: 'flex', flexDirection: 'row', gap: 1, alignItems:'center' }}>
        <Typography variant="body2">PENDING</Typography>
        <Typography variant="caption" color="warning.main">
          ({counts.pending})
        </Typography>
      </Box>
    }
  />

  <Tab
    label={
      <Box sx={{ textAlign: "center", lineHeight: 1.2, display: 'flex', flexDirection: 'row', gap: 1, alignItems:'center', }}>
        <Typography variant="body2">CONFIRMED</Typography>
        <Typography variant="caption" color="success.main">
          ({counts.confirmed})
        </Typography>
      </Box>
    }
  />

  <Tab
    label={
      <Box sx={{ textAlign: "center", lineHeight: 1.2, display: 'flex', flexDirection: 'row', gap: 1, alignItems:'center', }}>
        <Typography variant="body2">IN PROGRESS</Typography>
        <Typography variant="caption" color="info.main">
          ({counts.inProgress})
        </Typography>
      </Box>
    }
  />

  <Tab
    label={
      <Box sx={{ textAlign: "center", lineHeight: 1.2, display: 'flex', flexDirection: 'row', gap: 1, alignItems:'center', }}>
        <Typography variant="body2">COMPLETED</Typography>
        <Typography variant="caption" color="primary">
          ({counts.completed})
        </Typography>
      </Box>
    }
  />

  <Tab
    label={
      <Box sx={{ textAlign: "center", lineHeight: 1.2, display: 'flex', flexDirection: 'row', gap: 1, alignItems:'center',}}>
        <Typography variant="body2">CANCELLED</Typography>
        <Typography variant="caption" color="error.main">
          ({counts.cancelled})
        </Typography>
      </Box>
    }
  />
</Tabs>


          <TabPanel value={tabValue} index={tabValue}>
            <BookingTable
              bookings={filteredBookings}
              loading={loading}
              error={error}
              onStatusUpdate={handleStatusUpdate}
            />

<TablePagination
  rowsPerPageOptions={[5, 10, 25, 50]}
  component="div"
  count={totalItems}
  rowsPerPage={rowsPerPage}
  page={page}
  onPageChange={handleChangePage}
  onRowsPerPageChange={handleChangeRowsPerPage}
  sx={{
    '& .MuiTablePagination-toolbar': {
      paddingLeft: 0,
      paddingRight: 0,
      justifyContent: 'flex-between',
      color: 'text.primary'
    },
    '& .MuiTablePagination-displayedRows': {
      margin: 0,
      color: 'text.secondary'
    },
    '& .MuiTablePagination-selectLabel, & .MuiInputBase-root': {
      color: 'text.secondary'
    },
    '& .MuiSvgIcon-root': {
      color: 'text.secondary'
    }
  }}
/>

          </TabPanel>
        </Box>
      </Box>
    </Box>
  );
};

export default Bookings;
